// Input x Output token performance matrix, colour-coded slow -> fast.
import type { ColorMetric, MatrixCell } from '../api';
import { cellColor, formatMetric, METRIC_LABEL } from '../lib/colorScale';

interface Props {
  inputTokens: number[];
  outputTokens: number[];
  cells: MatrixCell[];
  colorMetric: ColorMetric;
  valueRange: { min: number | null; max: number | null };
  active?: { input?: number; output?: number } | null;
}

function tokenLabel(n: number): string {
  return n >= 1000 && n % 1000 === 0 ? `${n / 1000}k` : String(n);
}

export default function Heatmap({
  inputTokens,
  outputTokens,
  cells,
  colorMetric,
  valueRange,
  active,
}: Props) {
  const byKey = new Map(cells.map((c) => [`${c.inputTokens}x${c.outputTokens}`, c]));
  // Highest output at the top, like the reference graphic.
  const rows = [...outputTokens].sort((a, b) => b - a);

  const cols = { gridTemplateColumns: `auto repeat(${inputTokens.length}, 1fr)` };

  return (
    <div className="heatmap">
      {/* Three stacked rows that all share a fixed-width y-axis slot on the left,
          so the header columns line up with the data columns regardless of page,
          and the vertical y-axis label centres on the data grid on its own
          (flex stretch) instead of relying on a hand-tuned margin. */}
      <div className="hm-line">
        <div className="hm-yslot" />
        <div className="hm-cols" style={cols}>
          <div className="hm-corner" />
          <div
            className="hm-axis-x"
            style={{ gridColumn: '2 / -1' }}
            title="Prompt size in tokens (columns). Cells are colour-coded by the selected metric — the reference speed of one input × output point."
          >
            Input tokens →
          </div>
        </div>
      </div>
      <div className="hm-line">
        <div className="hm-yslot" />
        <div className="hm-cols" style={cols}>
          <div className="hm-corner" />
          {inputTokens.map((it) => (
            <div key={`h-${it}`} className="hm-col-label">
              {tokenLabel(it)}
            </div>
          ))}
        </div>
      </div>
      <div className="hm-line hm-data-line">
        <div
          className="hm-yslot hm-ylabel"
          title="Requested response length in tokens (rows). Cells where input + output + safety margin exceed the model's KV limit are skipped."
        >
          Output tokens →
        </div>
        <div className="hm-grid" style={cols}>
          {rows.map((ot) => (
            <FragmentRow
              key={`r-${ot}`}
              outputTokens={ot}
              inputTokens={inputTokens}
              byKey={byKey}
              colorMetric={colorMetric}
              valueRange={valueRange}
              active={active}
            />
          ))}
        </div>
      </div>

      <Legend metric={colorMetric} range={valueRange} />
    </div>
  );
}

function FragmentRow({
  outputTokens,
  inputTokens,
  byKey,
  colorMetric,
  valueRange,
  active,
}: {
  outputTokens: number;
  inputTokens: number[];
  active?: { input?: number; output?: number } | null;
  byKey: Map<string, MatrixCell>;
  colorMetric: ColorMetric;
  valueRange: { min: number | null; max: number | null };
}) {
  return (
    <>
      <div className="hm-row-label">{outputTokens}</div>
      {inputTokens.map((it) => {
        const cell = byKey.get(`${it}x${outputTokens}`);
        const skipped = cell?.skipped ?? false;
        const errored = cell?.errored ?? false;
        const value = cell?.value ?? null;
        const bg = skipped || errored ? undefined : cellColor(value, valueRange, colorMetric);
        const title = skipped
          ? `Skipped: ${cell?.skipReason ?? 'above KV limit'}`
          : errored
            ? `Failed: ${cell?.errorReason ?? 'the model returned no usable output'}`
            : cell
              ? [
                  `Input ${it} / Output ${outputTokens}`,
                  `Generation: ${formatMetric(cell.meanGenerateTps, 'generate_tps')} tok/s`,
                  `Thinking: ${formatMetric(cell.meanThinkTps, 'think_tps')} tok/s`,
                  `Prefill: ${formatMetric(cell.meanPrefillTps, 'prefill_tps')} tok/s`,
                  `TTFT: ${formatMetric(cell.meanTtftMs, 'ttft_ms')}`,
                  cell.meanTotalMs != null ? `Time: ${(cell.meanTotalMs / 1000).toFixed(1)} s` : '',
                  `n=${cell.nOk}`,
                ]
                  .filter(Boolean)
                  .join('\n')
              : 'Not measured yet';
        return (
          <div
            key={`${it}x${outputTokens}`}
            className={`hm-cell${skipped ? ' skipped' : ''}${errored ? ' errored' : ''}${
              !cell ? ' pending' : ''
            }${active && active.input === it && active.output === outputTokens ? ' active' : ''}`}
            style={bg ? { background: bg } : undefined}
            title={title}
          >
            {errored ? (
              <span className="hm-err">⚠</span>
            ) : !skipped && value != null ? (
              <span className="hm-value">{formatMetric(value, colorMetric)}</span>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

function Legend({
  metric,
  range,
}: {
  metric: ColorMetric;
  range: { min: number | null; max: number | null };
}) {
  return (
    <div className="legend">
      <span>slow</span>
      <span className="legend-bar" />
      <span>fast</span>
      <span className="legend-metric">
        {METRIC_LABEL[metric]}
        {range.min != null && range.max != null
          ? ` · ${formatMetric(range.min, metric)} – ${formatMetric(range.max, metric)}`
          : ''}
      </span>
    </div>
  );
}
